import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../../services/searchService';
import { skillService } from '../../services/skillService';
import './Search.css';

const Search = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    skills: [],
    proficiency_level: '',
    min_years_experience: '',
    department: '',
    location: '',
    keyword: ''
  });

  const [allSkills, setAllSkills] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    try {
      const response = await skillService.getAllSkills();
      // Handle both direct array and wrapped response
      const skills = Array.isArray(response) ? response : (response.data || []);
      setAllSkills(skills);
    } catch (err) {
      console.error('Failed to load skills:', err);
      setAllSkills([]);
    }
  };

  const handleSkillToggle = (skillId) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(id => id !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);

    try {
      const searchData = {
        skills: filters.skills.length > 0 ? filters.skills.map(id => {
          const skill = allSkills.find(s => s.id === id);
          return skill ? (skill.skill_name || skill.name) : null;
        }).filter(Boolean) : undefined,
        proficiencyLevels: filters.proficiency_level ? [filters.proficiency_level] : undefined,
        minYearsOfExperience: filters.min_years_experience || undefined,
        departments: filters.department ? [filters.department] : undefined,
        isActive: true
      };

      const response = await searchService.searchProfiles(searchData);
      // Handle paginated response: { success: true, data: { items: [...], pagination: {...} } }
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (response.data && response.data.items) {
        data = response.data.items;
      } else if (response.data && Array.isArray(response.data)) {
        data = response.data;
      }
      setResults(data);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      skills: [],
      proficiency_level: '',
      min_years_experience: '',
      department: '',
      location: '',
      keyword: ''
    });
    setResults([]);
    setSearched(false);
  };

  const skillsByCategory = Array.isArray(allSkills) 
    ? allSkills.reduce((acc, skill) => {
        if (!acc[skill.category]) acc[skill.category] = [];
        acc[skill.category].push(skill);
        return acc;
      }, {})
    : {};

  return (
    <div className="search-container">
      <div className="search-header">
        <h1>Search Profiles</h1>
        <p>Find employees by skills, experience, and more</p>
      </div>

      <div className="search-layout">
        <aside className="search-filters">
          <form onSubmit={handleSearch}>
            <div className="filter-section">
              <h3>Keyword Search</h3>
              <input
                type="text"
                placeholder="Search by name, position, bio..."
                value={filters.keyword}
                onChange={e => setFilters({...filters, keyword: e.target.value})}
                className="search-input"
              />
            </div>

            <div className="filter-section">
              <h3>Skills</h3>
              <div className="skills-filter">
                {Object.entries(skillsByCategory).map(([category, skills]) => (
                  <div key={category} className="skill-category-group">
                    <h4>{category}</h4>
                    <div className="skill-checkboxes">
                      {skills.map(skill => (
                        <label key={skill.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.skills.includes(skill.id)}
                            onChange={() => handleSkillToggle(skill.id)}
                          />
                          <span>{skill.skill_name || skill.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h3>Proficiency Level</h3>
              <select
                value={filters.proficiency_level}
                onChange={e => setFilters({...filters, proficiency_level: e.target.value})}
              >
                <option value="">Any Level</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            <div className="filter-section">
              <h3>Minimum Experience</h3>
              <input
                type="number"
                placeholder="Years"
                min="0"
                step="0.5"
                value={filters.min_years_experience}
                onChange={e => setFilters({...filters, min_years_experience: e.target.value})}
              />
            </div>

            <div className="filter-section">
              <h3>Department</h3>
              <input
                type="text"
                placeholder="e.g., Engineering"
                value={filters.department}
                onChange={e => setFilters({...filters, department: e.target.value})}
              />
            </div>

            <div className="filter-section">
              <h3>Location</h3>
              <input
                type="text"
                placeholder="e.g., New York"
                value={filters.location}
                onChange={e => setFilters({...filters, location: e.target.value})}
              />
            </div>

            <div className="filter-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
              <button type="button" onClick={handleReset} className="btn-secondary">
                Reset
              </button>
            </div>
          </form>
        </aside>

        <main className="search-results">
          {!searched ? (
            <div className="search-placeholder">
              <p>Use the filters to search for profiles</p>
            </div>
          ) : loading ? (
            <div className="loading">Searching...</div>
          ) : results.length > 0 ? (
            <>
              <div className="results-header">
                <h2>{results.length} Profile{results.length !== 1 ? 's' : ''} Found</h2>
              </div>
              <div className="results-grid">
                {results.map(profile => (
                  <div key={profile.id} className="result-card" onClick={() => navigate(`/profiles/${profile.id}`)}>
                    <div className="result-header">
                      <h3>{profile.full_name || `${profile.first_name} ${profile.last_name}`}</h3>
                      <span className="result-position">{profile.designation || profile.position}</span>
                    </div>
                    {profile.department && (
                      <p className="result-department">{profile.department}</p>
                    )}
                    {profile.location && (
                      <p className="result-location">📍 {profile.location}</p>
                    )}
                    {(profile.matched_skills || profile.skills) && (profile.matched_skills || profile.skills).length > 0 && (
                      <div className="result-skills">
                        {(profile.matched_skills || profile.skills).slice(0, 5).map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                        {(profile.matched_skills || profile.skills).length > 5 && (
                          <span className="skill-tag">+{(profile.matched_skills || profile.skills).length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              <p>No profiles found matching your criteria</p>
              <button onClick={handleReset} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Search;
